import type { AgentMessage, ChatMessage, ChatQuestionPayload } from './types'
import { createChatId, login } from '../utils'

export type StreamChunkHandler = (chunk: string) => void

const WC_API_BASE_URL = import.meta.env.VITE_DATASET_WOOCOMMERCE_API_URL
const SUPPORT_CHAT_AGENT_ID = import.meta.env.VITE_DATASET_WOOCOMMERCE_API_CHAT_AGENT_ID
const TEAM_ID = import.meta.env.VITE_DATASET_WOOCOMMERCE_API_TEAM_ID
const DATA_PREFIX = 'data:'
const BEARER_TOKEN_TTL_MS = 15 * 60 * 1000

let cachedBearerToken: string | null = null
let cachedBearerTokenExpiresAt = 0

type SupportChatStreamEvent =
  | {
      type: 'text-delta'
      id?: string
      delta?: string
    }
  | {
      type: 'error'
      errorText?: string
    }
  | {
      type: 'finish'
    }
  | {
      type: 'node_delta'
      delta?: {
        type?: string
        data?: unknown
      }
    }
  | {
      type: 'node_output'
      output?: {
        main?: unknown[]
      }
    }
  | {
      message?: {
        data?: string
      }
    }
  | Record<string, unknown>

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

const extractDeltaFromUnknownEvent = (event: unknown): string | null => {
  if (!isRecord(event)) return null

  if (event.type === 'text-delta' && typeof event.delta === 'string') {
    return event.delta
  }

  if (event.type === 'node_delta' && isRecord(event.delta) && event.delta.type === 'delta') {
    const nested = event.delta.data
    if (isRecord(nested) && nested.type === 'text-delta' && typeof nested.delta === 'string') {
      return nested.delta
    }
  }

  if (isRecord(event.message) && typeof event.message.data === 'string') {
    try {
      return extractDeltaFromUnknownEvent(JSON.parse(event.message.data))
    } catch {
      return null
    }
  }

  return null
}

const extractErrorFromUnknownEvent = (event: unknown): string | null => {
  if (!isRecord(event)) return null
  if (event.type !== 'error') return null
  return typeof event.errorText === 'string' ? event.errorText : 'Support chat stream error'
}

const extractFinalTextFromUnknownEvent = (event: unknown): string | null => {
  if (!isRecord(event) || event.type !== 'node_output' || !isRecord(event.output)) return null

  const { main } = event.output
  if (!Array.isArray(main)) return null

  const [firstGroup] = main
  if (!Array.isArray(firstGroup)) return null

  const [firstEntry] = firstGroup
  if (!isRecord(firstEntry) || !isRecord(firstEntry.json)) return null

  const { agent } = firstEntry.json
  if (!isRecord(agent) || typeof agent.text !== 'string') return null

  return agent.text.trim().length > 0 ? agent.text : null
}

const convertLatestQuestionToAgentMessages = (payload: ChatQuestionPayload): AgentMessage[] => {
  const latestMessage = payload.message.trim()
  if (latestMessage) {
    return [
      {
        role: 'user',
        content: [
          {
            type: 'text',
            text: latestMessage
          }
        ]
      }
    ]
  }

  const lastUserMessage = [...payload.messages].reverse().find(message => message.role === 'user' && message.content.trim().length > 0)
  if (!lastUserMessage) return []

  return [
    {
      role: 'user',
      content: [
        {
          type: 'text',
          text: lastUserMessage.content
        }
      ]
    }
  ]
}

const extractStreamEvent = (line: string): SupportChatStreamEvent | null => {
  const trimmedLine = line.trim()
  if (!trimmedLine.startsWith(DATA_PREFIX)) return null

  const payload = trimmedLine.replace(/^data:\s*/, '')
  if (!payload || payload === '[DONE]') return null

  const parsed = JSON.parse(payload) as unknown

  if (isRecord(parsed) && isRecord(parsed.message) && typeof parsed.message.data === 'string') {
    return JSON.parse(parsed.message.data) as SupportChatStreamEvent
  }

  return parsed as SupportChatStreamEvent
}

const getBearerToken = async (forceRefresh = false): Promise<string> => {
  if (!forceRefresh && cachedBearerToken && Date.now() < cachedBearerTokenExpiresAt) return cachedBearerToken

  const { bearerToken } = await login()
  cachedBearerToken = bearerToken
  cachedBearerTokenExpiresAt = Date.now() + BEARER_TOKEN_TTL_MS
  return bearerToken
}

const createSupportChatRequest = async (bearerToken: string, messages: AgentMessage[]) =>
  fetch(`${WC_API_BASE_URL}/api/v1/agent/${SUPPORT_CHAT_AGENT_ID}/run`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      Authorization: `Bearer ${bearerToken}`
    },
    body: JSON.stringify({
      input: [
        {
          json: {
            messages
          }
        }
      ],
      teamId: TEAM_ID,
      stream: true
    }),
    signal: AbortSignal.timeout(120_000)
  })

export const requestSupportAnswer = async (payload: ChatQuestionPayload, onChunk?: StreamChunkHandler): Promise<ChatMessage> => {
  const trimmedMessage = payload.message.trim()
  if (!trimmedMessage) {
    throw new Error('Support chat message cannot be empty')
  }

  if (!WC_API_BASE_URL || !SUPPORT_CHAT_AGENT_ID || !TEAM_ID) {
    throw new Error('Support chat environment variables are missing')
  }

  const latestQuestionOnlyMessages = convertLatestQuestionToAgentMessages(payload)

  if (latestQuestionOnlyMessages.length === 0) {
    throw new Error('Support chat message cannot be empty')
  }

  let bearerToken = await getBearerToken()
  let response = await createSupportChatRequest(bearerToken, latestQuestionOnlyMessages)

  if (response.status === 401 || response.status === 403) {
    cachedBearerToken = null
    cachedBearerTokenExpiresAt = 0
    bearerToken = await getBearerToken(true)
    response = await createSupportChatRequest(bearerToken, latestQuestionOnlyMessages)
  }

  if (!response.ok) {
    let errorMessage = `Support chat request failed (${response.status})`

    try {
      const errorPayload = (await response.json()) as { error?: string; details?: { message?: string } | string }

      if (typeof errorPayload.error === 'string') {
        errorMessage = errorPayload.error
      } else if (typeof errorPayload.details === 'string') {
        errorMessage = errorPayload.details
      } else if (errorPayload.details && typeof errorPayload.details.message === 'string') {
        errorMessage = errorPayload.details.message
      }
    } catch {
      const textFallback = await response.text().catch(() => '')
      if (textFallback.trim()) {
        errorMessage = textFallback
      }
    }

    throw new Error(errorMessage)
  }

  const reader = response.body?.pipeThrough(new TextDecoderStream()).getReader()
  if (!reader) {
    throw new Error('Support chat stream is not available')
  }

  let buffer = ''
  let aggregatedContent = ''
  let streamError: Error | null = null
  let hasStreamedTextDelta = false

  const processLine = (line: string) => {
    try {
      const event = extractStreamEvent(line)
      if (!event) return

      const delta = extractDeltaFromUnknownEvent(event)
      if (typeof delta === 'string' && delta.length > 0) {
        aggregatedContent += delta
        hasStreamedTextDelta = true
        onChunk?.(delta)
        return
      }

      const finalText = extractFinalTextFromUnknownEvent(event)
      if (!hasStreamedTextDelta && typeof finalText === 'string') {
        aggregatedContent += finalText
        hasStreamedTextDelta = true
        onChunk?.(finalText)
        return
      }

      const errorText = extractErrorFromUnknownEvent(event)
      if (errorText) {
        streamError = new Error(errorText)
      }
    } catch (error) {
      console.warn('[SUPPORT_CHAT] Failed to parse stream chunk', error)
    }
  }

  const readStream = async (): Promise<void> => {
    const { done, value } = await reader.read()
    if (done) {
      if (buffer.trim()) {
        processLine(buffer)
      }
      return
    }

    if (value) {
      buffer += value
      const lines = buffer.split(/\r?\n/)
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        processLine(line)
      }
    }

    await readStream()
  }

  await readStream()

  if (streamError) {
    throw streamError
  }

  if (!aggregatedContent) {
    throw new Error('Support chat did not return any content')
  }

  return {
    id: createChatId('assistant'),
    role: 'assistant',
    content: aggregatedContent,
    createdAt: new Date().toISOString()
  }
}
